import Paper, { PaperProps } from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import ErrorOutline from '@mui/icons-material/ErrorOutline'
import { AdCreative, AdCreativeWithUrls } from 'common/api/v1/types'
import { DATE_FORMAT_LONG, formatBitrate, formatFileSize, formatMs } from 'common/api/v1/helpers'
import { SxProps, Theme } from '@mui/material/styles'
import DataSet, { DataSetProps } from '../../../common/DataSet'
import { format } from 'date-fns'
import Stack from '@mui/material/Stack'
import Divider from '@mui/material/Divider'
import { AdCreativeThumbnail } from '../../../common/Thumbnail'
import { adCreativeIndicateStatusText, AdCreativeIndicatorIcon } from '../StatusIndicator'
import { styles } from '../styles'

interface MetadataProps {
  adCreative: AdCreativeWithUrls
  sx?: PaperProps['sx']
}

export const Metadata = ({ adCreative, sx }: MetadataProps) => {
  const data = {
    Id: adCreative.id,
    Created: format(new Date(adCreative.createdAt), DATE_FORMAT_LONG),
    Owner: adCreative.createdBy,
    Status: (
      <Stack direction={'row'} spacing={0.5} sx={styles.inlineWrapper}>
        <AdCreativeIndicatorIcon status={adCreative.status} />
        <Typography>{adCreativeIndicateStatusText(adCreative.status)}</Typography>
      </Stack>
    ),
  } as DataSetProps['values']

  const meta = adCreative.metadata
  if (meta) {
    if (meta.format?.durationMs) {
      data['Duration'] = formatMs(meta.format.durationMs)
    }

    if (meta.format?.bitrate) {
      data['Bitrate'] = formatBitrate(meta.format.bitrate)
    }

    if (meta.format?.fileSize) {
      data['Size'] = formatFileSize(meta.format.fileSize)
    }
  }

  return (
    <Paper sx={sx}>
      <Stack direction="row" sx={{ p: 1, width: '100%' }}>
        <Stack direction="column" spacing={2} sx={{ width: '100%', m: 2 }}>
          <Box sx={{ width: '100%', pl: 3, pt: 2 }}>
            <DataSet values={data} />
          </Box>

          <Divider />

          <Stack direction="row" spacing={15} sx={{ width: '70%', pl: 3, pt: 2, pb: 2 }}>
            <VideoMetadata metadata={adCreative.metadata} sx={{ border: 'none' }} />
            <AudioMetadata metadata={adCreative.metadata} sx={{ border: 'none' }} />
          </Stack>
        </Stack>

        {adCreative?.thumbnailUrl && (
          <Box
            overflow="hidden"
            sx={{
              width: '70%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              pr: 10,
              pl: 10,
            }}
          >
            <AdCreativeThumbnail adCreative={adCreative} />
          </Box>
        )}
      </Stack>
    </Paper>
  )
}

interface VideoMetadataProps {
  metadata: AdCreative['metadata']
  sx?: PaperProps['sx']
}

export const VideoMetadata = ({ metadata, sx }: VideoMetadataProps) => {
  const data = {
    Codec: metadata?.video?.codec,
    Profile: metadata?.video?.profile,
    Resolution: `${metadata?.video?.width}x${metadata?.video?.height}`,
    Framerate: metadata?.video?.framerate ? metadata.video.framerate.toFixed(2) : undefined,
    Bitrate: formatBitrate(metadata?.video?.bitrate),
  }

  return (
    <>
      {metadata?.video ? (
        <Paper variant="outlined" sx={[{ borderColor: 'primary.main' }, sx] as SxProps<Theme>}>
          <Typography variant="h4" gutterBottom>
            Video
          </Typography>
          <DataSet values={data} />
        </Paper>
      ) : (
        <Paper variant="outlined" sx={[{ borderColor: 'primary.main' }, sx] as SxProps<Theme>}>
          <Box display="flex" alignItems="center" gap={1}>
            <ErrorOutline color="error" />
            <Typography variant="h6" color="error">
              Video
            </Typography>
          </Box>
        </Paper>
      )}
    </>
  )
}

interface AudioMetadataProps {
  metadata: AdCreative['metadata']
  sx?: PaperProps['sx']
}

export const AudioMetadata = ({ metadata, sx }: AudioMetadataProps) => {
  const data = {
    Codec: metadata?.audio?.codec,
    Profile: metadata?.audio?.profile,
    Channels: metadata?.audio?.channels,
    Layout: metadata?.audio?.channelLayout,
    Bitrate: formatBitrate(metadata?.audio?.bitrate),
  }

  return (
    <>
      {metadata?.audio ? (
        <Paper
          variant="outlined"
          sx={[{ bgcolor: 'background.paper', borderColor: 'secondary.main' }, sx] as SxProps<Theme>}
        >
          <Typography variant="h3" gutterBottom>
            Audio
          </Typography>
          <DataSet values={data} />
        </Paper>
      ) : (
        <Paper
          variant="outlined"
          sx={[{ bgcolor: 'background.paper', borderColor: 'error.main' }, sx] as SxProps<Theme>}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <ErrorOutline color="error" />
            <Typography variant="h6" color="error">
              Audio
            </Typography>
          </Box>
        </Paper>
      )}
    </>
  )
}
